import { z } from 'zod';
import { stringify } from 'qs';
import { Platform } from 'react-native';
import Config from './config';
import { GetUrlPrefix, RequestOptions, Service, ServiceConfig } from './types';
import defaultHeaders from './defaultHeaders';
import { ApiError, ValidationError } from './Errors';
import { logHttpResponse, logInfo } from '../logging';
import { logHttpRequest } from '../logging/logging.web';

let refreshTokenPromise: Promise<string> | undefined;

export const createService = <T extends ServiceConfig>(
  serviceConfig: T,
  getUrlPrefix?: GetUrlPrefix,
  useBaseUrl: boolean = true,
): Service<T> => {
  const bindMethod =
    (config: T[keyof T]) =>
    async (
      options: RequestOptions<never> = {},
    ): Promise<z.infer<typeof config.responseSchema>> => {
      const userId = await Config.getUserId();
      const baseUrl = useBaseUrl ? await Config.getBaseUrl() : '';
      let urlPrefix = '';

      if (config.getMock) {
        return await config.getMock(options);
      }

      if (getUrlPrefix) {
        if (userId) {
          urlPrefix = await getUrlPrefix({ userId });
        } else {
          throw new Error('Missing userId');
        }
      }

      const configHeaders = Config.getRequestHeaders
        ? await Config.getRequestHeaders()
        : {};
      const headers: Record<string, string> = {
        ...defaultHeaders,
        ...configHeaders,
        ...config.headers,
        ...options.headers,
      };

      const queryString = stringify({
        ...config.params,
        ...options.params,
      });

      const methodUrl =
        typeof config.url === 'function'
          ? config.url(options.pathParams || ({} as never))
          : config.url;

      const url = `${baseUrl}${urlPrefix}${methodUrl}`;
      const finalUrl = queryString ? `${url}?${queryString}` : url;

      if (!config.public) {
        await Config.waitForRefreshToken(finalUrl);

        const token = await Config.getAccessToken();

        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }

      const request: RequestInit = {
        headers,
        method: config.method,
        credentials: config.credentials || 'include',
      };

      if (options.data) {
        if (headers['Content-Type'] === 'application/x-www-form-urlencoded') {
          const searchParams = new URLSearchParams();

          Object.keys(options.data).forEach((key) => {
            if (typeof options.data?.[key] !== 'object') {
              if (options.data?.[key] !== undefined) {
                searchParams.append(key, options.data?.[key]?.toString() || '');
              }
            } else {
              throw new Error('Unsupported nesting in form data');
            }
          });

          request.body = searchParams.toString();
        } else if (
          headers['Content-Type'] === 'multipart/form-data' &&
          options.data instanceof FormData
        ) {
          request.body = options.data;

          // If the platform is Web, let the browser set the Content-Type header including the required boundary.
          if (Platform.OS === 'web') {
            delete headers['Content-Type'];
          }
        } else {
          request.body = JSON.stringify(options.data);
        }
      }

      logHttpRequest(finalUrl, request);

      let response = await fetch(finalUrl, request);

      if (response.status === 401) {
        if (!refreshTokenPromise) {
          refreshTokenPromise = Config.refreshToken();
        }

        try {
          const newToken = await refreshTokenPromise;
          request.headers = {
            ...request.headers,
            Authorization: `Bearer ${newToken}`,
          };

          response = await fetch(finalUrl, request);
        } catch (e) {
          logInfo('auth', 'Refresh token failed', e);

          throw e;
        }

        refreshTokenPromise = undefined;
      }

      const responseText = await response.text();

      const jsonResponse = responseText ? JSON.parse(responseText) : undefined;

      logHttpResponse(response);

      if (!response.ok) {
        const error = new ApiError(
          `Request failed with status ${response.status}`,
          {
            status: response.status,
            url: finalUrl,
            request,
            data: jsonResponse as ApiError['data'],
          },
        );

        Config.onApiError?.(error);

        throw error;
      }

      try {
        config.responseSchema.parse(jsonResponse);
      } catch (e) {
        const error = new ValidationError('Response validation failed', e);

        Config.onApiError?.(error);

        throw error;
      }

      return jsonResponse;
    };

  return Object.keys(serviceConfig).reduce((services, method) => {
    (services as Record<string, unknown>)[method] = bindMethod(
      serviceConfig[method] as T[keyof T],
    );
    return services;
  }, {} as Service<T>);
};
